import {
	Create,
	Delete,
	KeyboardArrowLeft,
	KeyboardArrowRight,
	KeyboardDoubleArrowLeft,
	KeyboardDoubleArrowRight,
	Lock,
	Restore,
	Update,
	ZoomIn,
} from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	Checkbox,
	Divider,
	FormControl,
	FormControlLabel,
	Grid2,
	IconButton,
	ListItemText,
	MenuItem,
	Paper,
	Select,
	Switch,
	TextField,
	Tooltip,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQueryClient } from "@tanstack/react-query";
import { EChartsInstance } from "echarts-for-react";
import moment from "moment";
import React, { useState } from "react";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { SaveUpdateDeleteViewModalV2 } from "../components/modals/SaveUpdateDeleteViewModalV2";
import { useTrendingContextV2 } from "../context/TrendingContextV2";
import type {
	CustomOptions,
	View,
	scopeType,
} from "../models/TrendingModelsV2";
import { calculateCounterpartDate } from "../utils/calculateDateFromPeriod";
import { dateNavigator } from "../utils/dateNavigator";
import type { AggregationMode } from "../utils/trendingUrlState";

const AGGREGATION_LABELS: Record<AggregationMode, string> = {
	avg: "Avg",
	last: "Last",
	first: "First",
	min: "Min",
	max: "Max",
	envelope: "Envelope",
};

interface HeaderSectionProps {
	autoRefresh: boolean;
	setAutoRefresh: (value: boolean) => void;
	customOptions: CustomOptions;
	setCustomOptions: React.Dispatch<React.SetStateAction<CustomOptions>>;
	aggregationMode: AggregationMode;
	setAggregationMode: (mode: AggregationMode) => void;
	chartInstance: EChartsInstance | null;
}

export const HeaderSectionV2: React.FC<HeaderSectionProps> = React.memo(
	({
		autoRefresh,
		setAutoRefresh,
		customOptions,
		setCustomOptions,
		aggregationMode,
		setAggregationMode,
		chartInstance,
	}) => {
		const queryClient = useQueryClient();
		const {
			state: { timeScopeStart, timeScopeEnd, scope, viewSelected, views },
			actions: { setTotalScope, setViewSelected },
		} = useTrendingContextV2();

		// View management state
		const [viewModalMode, setViewModalMode] = useState<
			"create" | "update" | "delete" | null
		>(null);
		const [viewForModal, setViewForModal] = useState<View | null>(null);
		const [error, setError] = useState("");

		const handleDateNavigator = (
			operator: "add" | "subtract",
			current = false,
		) => {
			const newDates = dateNavigator(
				timeScopeStart,
				timeScopeEnd,
				scope,
				operator,
				current,
			);
			if (newDates) {
				setTotalScope({
					start: newDates.newStartDate,
					end: newDates.newEndDate,
					scope: scope,
				});
			}
		};

		const handlePartialDateNavigator = (operator: "add" | "subtract") => {
			// Calculate 20% of the current period
			const durationInMs = moment(timeScopeEnd).diff(moment(timeScopeStart));
			const partialDuration = Math.round(durationInMs * 0.2);

			const newStartDate = moment(timeScopeStart)
				[operator](partialDuration, "milliseconds")
				.toDate();
			const newEndDate = moment(timeScopeEnd)
				[operator](partialDuration, "milliseconds")
				.toDate();

			setTotalScope({
				start: newStartDate,
				end: newEndDate,
				scope: scope,
			});
		};

		const handleDateChange = (newValue: any, key: "start" | "end") => {
			const newDate = moment(newValue).toDate();

			// If scope is "custom", just update the changed date
			if (scope === "custom") {
				setTotalScope({ [key]: newDate });
				return;
			}

			// Calculate the counterpart date based on the period
			const calculatedDates = calculateCounterpartDate(newDate, key, scope);

			if (calculatedDates) {
				// Update both start and end to maintain the period duration
				setTotalScope({
					start: calculatedDates.start,
					end: calculatedDates.end,
					scope: scope, // Keep the same scope
				});
			} else {
				// Fallback: just update the single date
				setTotalScope({ [key]: newDate });
			}
		};

		const handleChangeScope = (value: scopeType) => {
			if (value === "custom") {
				// Just update scope to custom, keep current dates
				setTotalScope({
					scope: value,
				});
				return;
			}

			// Keep the current START date and calculate END based on new period
			const calculatedDates = calculateCounterpartDate(
				timeScopeStart,
				"start",
				value,
			);

			if (calculatedDates) {
				setTotalScope({
					start: calculatedDates.start, // Keep the same start
					end: calculatedDates.end, // Calculate new end
					scope: value,
				});
			}
		};

		// View management handlers
		const handleViewChange = async (
			event: React.ChangeEvent<{}>,
			value: View | null,
		) => {
			if (value) {
				setViewSelected(value);
				// Invalidate the viewTags query to trigger a refetch with the new viewId
				await queryClient.invalidateQueries({
					queryKey: ["viewTags-v2", value.ViewId],
				});
			}
		};

		const handleDeleteView = (view: View, e: React.MouseEvent) => {
			e.stopPropagation();
			if (view.ViewId === viewSelected?.ViewId) {
				setError("Cannot delete current view");
			} else {
				setViewForModal(view);
				setViewModalMode("delete");
			}
		};

		const handleEditView = (view: View, e: React.MouseEvent) => {
			e.stopPropagation();
			setViewForModal(view);
			setViewModalMode("update");
		};

		// Zoom control handlers
		const handleZoomIn = () => {
			if (!chartInstance || (chartInstance as any)._disposed) return;

			chartInstance.dispatchAction({
				type: "takeGlobalCursor",
				key: "dataZoomSelect",
				dataZoomSelectActive: true,
			});
		};

		const handleRestoreZoom = () => {
			if (!chartInstance || (chartInstance as any)._disposed) return;

			chartInstance.dispatchAction({
				type: "restore",
			});
		};

		return (
			<>
				<Grid2
					size={12}
					container
					spacing={1}
					justifyContent="space-between"
					alignItems="center"
					style={{ height: "auto" }}
				>
					{/* Left side: Date pickers and navigation */}
					<Grid2
						size={{ md: 5, sm: 12, xs: 12 }}
						container
						justifyContent="flex-start"
						alignItems="center"
						wrap="wrap"
					>
						<Grid2
							container
							spacing={2}
							component={Paper}
							elevation={0}
							style={{
								backgroundColor: "white",
								display: "flex",
								flexWrap: "wrap",
								gap: "4px",
								alignItems: "center",
								width: "100%",
							}}
						>
							<Grid2>
								<Tooltip title="Previous period" arrow>
									<IconButton
										onClick={() => handleDateNavigator("subtract")}
										size="small"
									>
										<KeyboardDoubleArrowLeft
											fontSize="medium"
											sx={{ color: "black" }}
										/>
									</IconButton>
								</Tooltip>
							</Grid2>

							<Grid2>
								<Tooltip title="Pan backward 20%" arrow>
									<IconButton
										onClick={() => handlePartialDateNavigator("subtract")}
										size="small"
									>
										<KeyboardArrowLeft
											fontSize="medium"
											sx={{ color: "black" }}
										/>
									</IconButton>
								</Tooltip>
							</Grid2>

							<Grid2 size={{ md: 3.5 }}>
								<LocalizationProvider dateAdapter={AdapterMoment}>
									<DateTimePicker
										label="Start"
										format="MM/DD/YYYY HH:mm:ss"
										value={moment(timeScopeStart)}
										onChange={(newValue) => {
											if (newValue) {
												handleDateChange(newValue.toDate(), "start");
											}
										}}
										minutesStep={1}
										slotProps={{
											textField: {
												size: "small",
												variant: "outlined",
												fullWidth: true,
											},
										}}
									/>
								</LocalizationProvider>
							</Grid2>

							<Grid2 size={{ md: 2 }} marginTop={0.5}>
								<FormControl variant="outlined" fullWidth size="small">
									<Select
										value={scope}
										onChange={(e) =>
											handleChangeScope(e.target.value as scopeType)
										}
										displayEmpty
									>
										<MenuItem value="1 min">1 min</MenuItem>
										<MenuItem value="10 min">10 min</MenuItem>
										<MenuItem value="30 min">30 min</MenuItem>
										<MenuItem value="1 hour">1 hour</MenuItem>
										<MenuItem value="4 hours">4 hours</MenuItem>
										<MenuItem value="12 hours">12 hours</MenuItem>
										<MenuItem value="1 day">1 day</MenuItem>
										<MenuItem value="3 days">3 days</MenuItem>
										<MenuItem value="10 days">10 days</MenuItem>
										<MenuItem value="custom">Custom</MenuItem>
									</Select>
								</FormControl>
							</Grid2>

							<Grid2 size={{ md: 3.5 }}>
								<LocalizationProvider dateAdapter={AdapterMoment}>
									<DateTimePicker
										label="End"
										format="MM/DD/YYYY HH:mm:ss"
										value={autoRefresh ? null : moment(timeScopeEnd)}
										onChange={(newValue) => {
											if (newValue) {
												handleDateChange(newValue.toDate(), "end");
											}
										}}
										minutesStep={1}
										slotProps={{
											textField: {
												size: "small",
												variant: "outlined",
												fullWidth: true,
											},
										}}
										disabled={autoRefresh}
									/>
								</LocalizationProvider>
							</Grid2>

							<Grid2>
								<Tooltip title="Pan forward 20%" arrow>
									<IconButton
										onClick={() => handlePartialDateNavigator("add")}
										size="small"
									>
										<KeyboardArrowRight
											fontSize="medium"
											sx={{ color: "black" }}
										/>
									</IconButton>
								</Tooltip>
							</Grid2>
							<Grid2>
								<Tooltip title="Next period" arrow>
									<IconButton
										onClick={() => handleDateNavigator("add")}
										size="small"
									>
										<KeyboardDoubleArrowRight
											fontSize="medium"
											sx={{ color: "black" }}
										/>
									</IconButton>
								</Tooltip>
							</Grid2>

							<Grid2>
								<Tooltip title="Jump to current time" arrow>
									<IconButton
										onClick={() => handleDateNavigator("subtract", true)}
										size="small"
									>
										<Update fontSize="medium" sx={{ color: "black" }} />
									</IconButton>
								</Tooltip>
							</Grid2>
						</Grid2>
					</Grid2>

					{/* Chart Options */}
					<Grid2 size={{ md: 5, sm: 6, xs: 12 }}>
						<Paper
							elevation={0}
							sx={{
								p: 1,
								overflow: "auto",
							}}
						>
							{/* Container with chart options dropdown, divider, and toggles */}
							<div
								style={{
									display: "flex",
									gap: "8px",
									alignItems: "stretch",
									justifyContent: "space-between",
								}}
							>
								{/* Left side - Multiple Select Dropdown */}
								<Box sx={{ width: 155 }}>
									<FormControl fullWidth size="small">
										<Select
											multiple
											value={[
												...(customOptions.showGridLines ? ["grid"] : []),
												...(customOptions.smoothLines ? ["smooth"] : []),
												...(customOptions.steppedLines ? ["stepped"] : []),
												...(customOptions.showTooltip ? ["tooltip"] : []),
												...(customOptions.showToolbox ? ["toolbox"] : []),
												...(customOptions.showYAxisNames ? ["yAxisNames"] : []),
											]}
											onChange={(e) => {
												const selected = e.target.value as string[];
												setCustomOptions((prevOptions) => ({
													...prevOptions,
													showGridLines: selected.includes("grid"),
													smoothLines:
														selected.includes("smooth") &&
														!selected.includes("stepped"),
													steppedLines: selected.includes("stepped"),
													showTooltip: selected.includes("tooltip"),
													showToolbox: selected.includes("toolbox"),
													showYAxisNames: selected.includes("yAxisNames"),
												}));
											}}
											renderValue={(selected) => (
												<span style={{ fontSize: "0.75rem" }}>
													Chart: {(selected as string[]).length} options
												</span>
											)}
										>
											<MenuItem value="grid" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.showGridLines}
													size="small"
												/>
												<ListItemText>Grid</ListItemText>
											</MenuItem>
											<MenuItem value="smooth" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.smoothLines}
													size="small"
												/>
												<ListItemText>Smooth</ListItemText>
											</MenuItem>
											<MenuItem value="stepped" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.steppedLines}
													size="small"
												/>
												<ListItemText>Stepped</ListItemText>
											</MenuItem>
											<MenuItem value="tooltip" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.showTooltip}
													size="small"
												/>
												<ListItemText>Tooltip</ListItemText>
											</MenuItem>
											<MenuItem value="toolbox" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.showToolbox}
													size="small"
												/>
												<ListItemText>Toolbox</ListItemText>
											</MenuItem>
											<MenuItem value="yAxisNames" style={{ padding: 0 }}>
												<Checkbox
													checked={customOptions.showYAxisNames}
													size="small"
												/>
												<ListItemText>Y-Axis Names</ListItemText>
											</MenuItem>
										</Select>
									</FormControl>
								</Box>

								<Box sx={{ minWidth: 150 }}>
									<FormControl fullWidth size="small">
										<Select
											value={aggregationMode}
											onChange={(e) => {
												setAggregationMode(e.target.value as AggregationMode);
											}}
											renderValue={(selected) =>
												`Agg: ${AGGREGATION_LABELS[selected as AggregationMode]}`
											}
										>
											<MenuItem value="avg">Average</MenuItem>
											<MenuItem value="last">Last value</MenuItem>
											<MenuItem value="first">First value</MenuItem>
											<MenuItem value="min">Min value</MenuItem>
											<MenuItem value="max">Max value</MenuItem>
											<MenuItem value="envelope">Envelope</MenuItem>
										</Select>
									</FormControl>
								</Box>

								{/* Vertical Divider */}
								<Divider orientation="vertical" flexItem />

								{/* Combined View Toggle */}
								<FormControlLabel
									checked={customOptions.combinedView}
									control={<Switch color="primary" size="small" />}
									label={
										<span style={{ fontSize: "0.75rem", fontWeight: 600 }}>
											Combined
										</span>
									}
									style={{ margin: "0" }}
									onChange={(e, checked) => {
										setCustomOptions((prevOptions) => ({
											...prevOptions,
											combinedView: checked,
										}));
									}}
								/>

								{/* Auto Refresh Toggle */}
								<FormControlLabel
									checked={autoRefresh}
									control={<Switch color="primary" size="small" />}
									label={
										<span style={{ fontSize: "0.75rem", fontWeight: 600 }}>
											Auto
										</span>
									}
									style={{ margin: "0" }}
									onChange={(e, checked) => {
										if (checked) {
											// When enabling auto-refresh, set end to now
											// and keep the current period to calculate start
											const now = new Date();
											if (scope !== "custom") {
												const calculatedDates = calculateCounterpartDate(
													now,
													"end",
													scope,
												);
												if (calculatedDates) {
													setTotalScope({
														start: calculatedDates.start,
														end: calculatedDates.end,
														scope: scope,
													});
												}
											}
										}
										setAutoRefresh(checked);
									}}
								/>

								{/* Second Vertical Divider */}
								<Divider orientation="vertical" flexItem />

								{/* Raw Data Toggle */}
								<FormControlLabel
									checked={customOptions.rawData}
									control={<Checkbox color="primary" size="small" />}
									label={
										<span style={{ fontSize: "0.75rem", fontWeight: 600 }}>
											Raw data
										</span>
									}
									style={{ margin: "0" }}
									onChange={(e, checked) => {
										setCustomOptions((prevOptions) => ({
											...prevOptions,
											rawData: checked,
										}));
									}}
								/>

								{/* Third Vertical Divider */}
								<Divider orientation="vertical" flexItem />

								<Tooltip title="Area zoom" arrow>
									<IconButton
										color="primary"
										onClick={handleZoomIn}
										size="small"
									>
										<ZoomIn fontSize="small" />
									</IconButton>
								</Tooltip>
								<Tooltip title="Restore zoom" arrow>
									<IconButton
										color="primary"
										onClick={handleRestoreZoom}
										size="small"
									>
										<Restore fontSize="small" />
									</IconButton>
								</Tooltip>
							</div>
						</Paper>
					</Grid2>

					{/* View Selector - Inline Autocomplete */}
					<Grid2
						size={{ md: 2, sm: 6, xs: 12 }}
						container
						justifyContent="flex-end"
						alignItems="center"
					>
						<Paper
							elevation={0}
							sx={{
								p: 1,
								backgroundColor: "white",
								width: "100%",
							}}
						>
							{/* Autocomplete View Selector */}
							<Autocomplete
								size="small"
								id="view-selector"
								options={views}
								getOptionLabel={(option) => option.ViewName}
								value={viewSelected}
								isOptionEqualToValue={(option, value) =>
									option.ViewId === value?.ViewId
								}
								onChange={handleViewChange}
								noOptionsText="No views available"
								renderOption={(props, option) => (
									<Box
										{...props}
										component="li"
										style={{
											display: "flex",
											justifyContent: "space-between",
											width: "100%",
										}}
									>
										<div
											style={{
												flex: 1,
												overflow: "hidden",
												textOverflow: "ellipsis",
											}}
										>
											{option.ViewName}
										</div>
										<div
											style={{
												display: "flex",
												gap: "2px",
											}}
										>
											{!option.IsPublic && (
												<Tooltip title="Private" arrow>
													<Lock
														fontSize="small"
														style={{
															height: "16px",
															width: "16px",
															color: "rgba(0, 0, 0, 0.54)",
														}}
													/>
												</Tooltip>
											)}
											<IconButton
												size="small"
												onClick={(e) => handleEditView(option, e)}
											>
												<Create
													fontSize="small"
													color="primary"
													style={{ height: "16px", width: "16px" }}
												/>
											</IconButton>
											<IconButton
												size="small"
												onClick={(e) => handleDeleteView(option, e)}
											>
												<Delete
													fontSize="small"
													color="secondary"
													style={{ height: "16px", width: "16px" }}
												/>
											</IconButton>
										</div>
									</Box>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										label="View"
										variant="outlined"
										size="small"
										style={{ minWidth: "100%" }}
									/>
								)}
							/>
						</Paper>
					</Grid2>
				</Grid2>

				{/* View Management Modal (Create/Update/Delete) */}
				{viewModalMode && (
					<SaveUpdateDeleteViewModalV2
						open={true}
						mode={viewModalMode}
						view={viewForModal}
						handleClose={async (shouldUpdate: boolean) => {
							setViewModalMode(null);
							setViewForModal(null);
							if (shouldUpdate) {
								await queryClient.invalidateQueries({ queryKey: ["views-v2"] });
							}
						}}
					/>
				)}

				{/* Error Modal */}
				<ErrorModal error={error} onHide={() => setError("")} />
			</>
		);
	},
);

HeaderSectionV2.displayName = "HeaderSectionV2";
